/**
 *  @class
 *  @function FacetsToggle
 */
class FacetsToggle {
  constructor() {
    this.container = document.getElementById('Facet-Drawer');
    let button = document.getElementById('Facets-Toggle');

    // Add functionality to buttons
    if (button) {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        document.getElementsByTagName('body')[0].classList.toggle('open-cc');
        this.container.classList.toggle('active');
      });
    }
    setTimeout(function () {
      window.dispatchEvent(new Event('resize.resize-select'));
    }, 10);
  }
}

const getUrlParams = () => {
  if (!location.href.includes('?') || location.href.split('?').length < 2) return null;
  return new URLSearchParams(location.href.split('?')[1]);
};

let pageFacetsLoaded = false;

class FacetFiltersForm extends HTMLElement {
  #postcode = '';
  #appliedDeliveryFilter = '';

  // Constants
  static STORE = 'us';
  static SDD = 'SDD';
  static CC = 'CC';
  static DELIVERY_METHOD_ENDPOINT = `${window.location.origin}/a/shipping/delivery-method/eligibility`;

  static POSTCODE_STORAGE = 'delivery_postcode';

  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 500);

    this.querySelector('form').addEventListener(
      'input',
      this.debouncedOnSubmit.bind(this)
    );

    this.#onFacetsRefresh();

    document.addEventListener(
      'DOMContentLoaded',
      debounce(this.#onPageLoad.bind(this), 500)
    );
  }

  async #onPageLoad() {
    if (pageFacetsLoaded) return;

    pageFacetsLoaded = true;

    const params = getUrlParams();

    // Get the list of all available stores
    if (
      params &&
      params.size &&
      params.get('postcode-field') &&
      params.get('delivery-filter-options')
    ) {
      const availableStores = await this.#processDeliveryFilter(
        params.get('delivery-filter-options'),
        params.get('postcode-field'),
        false
      );

      document.querySelector(`#Filter-Delivery-Available-Stores`).value =
        availableStores.toString();
    }

    this.#onFacetsRefresh();
  }

  /**
   * Sets the value of the postcode on the object level as well as saves it on the local storage level.
   * This local storage data is used to pre-fill the postcode field on each product details page on load.
   *
   * @param postcode
   */
  setPostcode(postcode) {
    this.#postcode = postcode;

    localStorage.setItem(FacetFiltersForm.POSTCODE_STORAGE, this.#postcode);
  }

  /**
   * Set delivery filter values retrieved from the URL parameter.
   * This is used to fill the current delivery filter value to the page after facets refresh.
   * As part of the process, it puts the available postcode value on the input box, selects the delivery method available
   * on the URL, marks the selected stores and displays them if required (it's displayed for CC but not SDD).
   * This function doesn't apply the filter value but makes sure that the UI has correct filter items selected.
   */
  #onFacetsRefresh() {
    if (!pageFacetsLoaded) return;

    let params = getUrlParams();

    if (!params || !params.size || !params.get('postcode-field')) {
      this.setPostcode('');

      return;
    }

    // When the postcode value is not available during initial facets refresh.
    params = this.#processPostcodeOnRefresh(params);

    if (this.#appliedDeliveryFilter) this.#resetDeliveryStores();

    // If no delivery filter is applied then we skip the store selection process.
    this.#appliedDeliveryFilter =
      document.querySelector('input[name="delivery-filter-options"]:checked')
        ?.value ?? params.get('delivery-filter-options');

    if (!this.#appliedDeliveryFilter) return;

    // Select the delivery filter available on the URL request.
    document.querySelector(
      `#${this.#appliedDeliveryFilter}-input`
    ).checked = true;

    this.#processDeliveryStores(params);
  }

  /**
   * Handles setting up postcode value on UI based on the provided URL value as well as call postcode entry action handler
   * to make sure the delivery filters are available on valid postcode entry.
   * This method can be used on both page load and facets refresh.
   */
  #processPostcodeOnRefresh(urlSearchParams) {
    if (!document.querySelector('input[name="postcode-field"]').value) {
      document.querySelector('input[name="postcode-field"]').value =
        urlSearchParams.get('postcode-field');
    }

    return this.handlePostcodeEntry(
      document.querySelector('input[name="postcode-field"]').value,
      urlSearchParams
    );
  }

  /**
   * Ensures available delivery stores are either set to be visible or selected as required.
   * For CC delivery method, all available stores are set to be visible but only the ones that are available as
   * `filter.available.stores` value are selected. However, for SDD selection, every single available stores are automatically
   * selected but not visible on the UI.
   * @param urlSearchParams
   */
  #processDeliveryStores(urlSearchParams) {
    // Get the available stores list, first (if available) from the input field or from the URL.
    const availableStoresFromInputField = document.querySelector(
      `#Filter-Delivery-Available-Stores`
    )?.value;
    const availableStoresFromUrl = urlSearchParams.get(
      'filter.available.stores'
    )
      ? urlSearchParams.get('filter.available.stores').split(',')
      : [];

    const availableStores = availableStoresFromInputField
      ? availableStoresFromInputField.split(',')
      : availableStoresFromUrl;

    if (
      urlSearchParams.has('filter.available.stores') &&
      !availableStores.length
    ) {
      this.handleDeliveryErrorMessage(this.#appliedDeliveryFilter, true);

      return;
    }

    const activeFacetValues = this.getActiveFilterValues();
    const selectedStores = urlSearchParams.getAll('filter.v.m.custom.stores')
      ? urlSearchParams.getAll('filter.v.m.custom.stores')
      : [];

    // Go through every available store and mark every selected ones as checked on the UI.
    for (let store of availableStores) {
      store = !isNaN(store) ? store : store.match(/^\d+/)[0];

      if (this.#appliedDeliveryFilter === 'cc-filter') {
        const storeContainer = document.querySelector(
          `#Filter-Container-Delivery-${store}`
        );

        if (!storeContainer) continue;

        storeContainer.style.display = 'block';

        const filterStoreNumber = storeContainer
          .querySelector(`[name="filter.v.m.custom.stores"]`)
          ?.getAttribute('data-store-number');

        const storeName = document
          .querySelector(`#delivery-store-label-${filterStoreNumber}`)
          ?.innerHTML?.trim();

        const storeInputBox = document.querySelector(
          `#Filter-Delivery-${store}`
        );

        if (!storeInputBox.checked) {
          storeInputBox.checked =
            selectedStores.includes(storeInputBox.value) ||
            (storeName && !!activeFacetValues.includes(storeName));
        }

        continue;
      }

      const storeCheckbox = document.querySelector(`#Filter-Delivery-${store}`);

      if (storeCheckbox) storeCheckbox.checked = true;
    }
  }

  /**
   * Provides list of all values that are currently applied as filter items.
   */
  getActiveFilterValues() {
    const filterValues = [];

    document.querySelectorAll('.active-facets').forEach((activeFacet) => {
      const filterValue = activeFacet
        .querySelector('.active-delivery-filter-value')
        ?.getAttribute('data-filter-value');

      if (filterValue) filterValues.push(filterValue);
    });

    return filterValues;
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state
        ? event.state.searchParams
        : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const container = document.getElementsByClassName('thb-filter-count');
    document
      .getElementById('ProductGridContainer')
      .querySelector('.collection')
      .classList.add('loading');

    for (var item of container) {
      item.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      if (FacetFiltersForm.filterData.some(filterDataUrl)) {
        FacetFiltersForm.renderSectionFromCache(filterDataUrl, event);
      } else {
        FacetFiltersForm.renderSectionFromFetch(url, event);
      }
    });
    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [
          ...FacetFiltersForm.filterData,
          {
            html,
            url,
          },
        ];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);

        new FacetsToggle();
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);

    new FacetsToggle();
  }

  static renderProductGridContainer(html) {
    document.getElementById('ProductGridContainer').innerHTML = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductGridContainer').innerHTML;

    let top =
      document.getElementById('ProductGridContainer').getBoundingClientRect()
        .top +
      document.documentElement.scrollTop -
      parseInt(
        getComputedStyle(document.documentElement).getPropertyValue(
          '--header-height'
        ),
        10
      ) -
      30;

    window.scrollTo({
      top: top,
      left: 0,
      behavior: 'smooth',
    });
  }

  static renderProductCount(html) {
    const countHtml = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductCount');
    const container = document.getElementsByClassName('thb-filter-count');

    if (countHtml) {
      for (var item of container) {
        item.innerHTML = countHtml.innerHTML;
        item.classList.remove('loading');
      }
    }
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');

    const facetDetailsElements = parsedHTML.querySelectorAll(
      '#FacetFiltersForm collapsible-row, #FacetFiltersFormMobile collapsible-row'
    );
    const matchesIndex = (element) => {
      const jsFilter = event
        ? event.target.closest('collapsible-row')
        : undefined;
      return jsFilter
        ? element.dataset.index === jsFilter.dataset.index
        : false;
    };
    const facetsToRender = Array.from(facetDetailsElements).filter(
      (element) => !matchesIndex(element)
    );
    const countsToRender = Array.from(facetDetailsElements).find(matchesIndex);

    facetsToRender.forEach((element) => {
      document.querySelector(
        `collapsible-row[data-index="${element.dataset.index}"]`
      ).innerHTML = element.innerHTML;
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);
    new FacetsToggle();

    if (countsToRender)
      FacetFiltersForm.renderCounts(
        countsToRender,
        event.target.closest('collapsible-row')
      );
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML =
        activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-filter-count'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML =
        html.querySelector(selector).innerHTML;
    });
  }

  static renderCounts(source, target) {
    const targetElement = target.querySelector('.facets__selected');
    const sourceElement = source.querySelector('.facets__selected');

    if (sourceElement && targetElement) {
      target.querySelector('.facets__selected').outerHTML =
        source.querySelector('.facets__selected').outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState(
      {
        searchParams,
      },
      '',
      `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`
    );
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  /**
   * Handles processing of delivery filter when a postcode is provided.
   * If the provided postcode is valid then makes the delivery filter options available for selection, otherwise shows an error message to the user.
   * Postcode is valid if it's a four digits numeric value (since valid postcodes in AU and NZ are 4 digits long).
   **/
  handlePostcodeEntry(postcodeCandidate, searchParams) {
    // Provided value is a numeric value.
    this.setPostcode(postcodeCandidate);

    if (isNaN(postcodeCandidate)) {
      this.#toggleDeliveryFilter(true, true);
      return this.unselectAllDeliveryMethods(searchParams);
    }

    const postcodeLength = postcodeCandidate?.toString().length;

    if (postcodeLength && postcodeLength !== 4) {
      // Invalid postcode length.
      this.#toggleDeliveryFilter(true, postcodeLength > 4);
      return this.unselectAllDeliveryMethods(searchParams);
    }

    this.#toggleDeliveryFilter(false, true);

    return searchParams;
  }

  /**
   * Unselects all available delivery methods and removes them from the URL params.
   *
   * @param searchParams
   * @return {*}
   */
  unselectAllDeliveryMethods(searchParams) {
    ['cc-filter', 'sdd-filter'].forEach((filterItem) => {
      document.querySelector(`#${filterItem}-input`).checked = false;
    });

    if (searchParams.get('delivery-filter-options'))
      searchParams.delete('delivery-filter-options');
    if (searchParams.get('filter.v.m.custom.stores'))
      searchParams.delete('filter.v.m.custom.stores');

    return searchParams;
  }

  /**
   * Toggles available delivery filter options.
   * Determines if the delivery options should be displayed based on the provided `hide` flag.
   **/
  #toggleDeliveryFilter(
    hideDeliveryMethods = true,
    errorMessageToggle = false
  ) {
    const errorMessageDisplay = hideDeliveryMethods ? 'block' : 'none';

    if (errorMessageToggle)
      document.querySelector('#delivery-filter-error').style.display =
        errorMessageDisplay;

    // Toggle visibility of the delivery filters.
    if (!hideDeliveryMethods) {
      document
        .querySelector(`#delivery-filter-options`)
        .classList.remove('hiding');
    } else {
      document
        .querySelector(`#delivery-filter-options`)
        .classList.add('hiding');
    }

    if (hideDeliveryMethods) {
      // Hide all stores
      document
        .querySelectorAll(
          '.delivery-filter-values:is([style*="display:block"])'
        )
        .forEach((store) => {
          store.style.display = 'none';
        });
    }
  }

  /**
   * Makes a request to get stores eligible for either SDD or CC for the given postcode.
   **/
  async #fetchDeliveryStores(selectedFilter, postcode) {
    const deliveryType =
      selectedFilter === 'sdd-filter'
        ? FacetFiltersForm.SDD
        : FacetFiltersForm.CC;

    const response = await fetch(
      `${FacetFiltersForm.DELIVERY_METHOD_ENDPOINT}?postcode=${postcode}&delivery_type=${deliveryType}&store=${FacetFiltersForm.STORE}`
    );

    return (await response.json())?.data ?? [];
  }

  /**
   * Resets (unselects them) all available stores for CC/SDD selection.
   * This method only affects the select/un-select UI part of the delivery filter and doesn't actually rerun the filter
   * for the present collection.
   */
  #resetDeliveryStores(resetActiveFilter = true) {
    // Unselect all stores from the filter list
    Array.from(document.querySelectorAll(`.delivery-filter-values`)).forEach(
      (storeElement) => {
        storeElement.style.display = 'none';

        storeElement.querySelector(
          `input[name="filter.v.m.custom.stores"]`
        ).checked = false;
      }
    );

    if (!resetActiveFilter) return;

    // Remove all stores from the active filter list (available on facets-bar section)
    Array.from(
      document.querySelectorAll(`.active-delivery-filter-value`)
    ).forEach((storeElement) => storeElement.parentElement.remove());
    this.#appliedDeliveryFilter = '';
  }

  /**
   * For the given postcode, gets the list of stores eligible for either SDD or CC,
   * and provides result on the collection page based on the eligible stores.
   * The actual process involves either selecting the list of stores on the filter options for SDD (to facilitate automated filtering),
   * or provide the list of eligible stores to select for CC
   **/
  async #processDeliveryFilter(
    selectedFilter,
    postcode,
    resetActiveFilter = true
  ) {
    document
      .querySelector(
        `#Facet-Drawer .thb-filter-count.mobile-filter-count.body-font`
      )
      .classList.add('loading');
    this.#resetDeliveryStores(resetActiveFilter);

    let processedStores = [];

    (await this.#fetchDeliveryStores(selectedFilter, postcode)).forEach(
      (store) => {
        let storeAvailable = false;
        // For SDD, auto-select the store option but for CC just make it visible.
        switch (selectedFilter) {
          case 'sdd-filter':
            const deliveryOption = document.querySelector(
              `#Filter-Delivery-${store}`
            );
            if (deliveryOption)
              [deliveryOption.checked, storeAvailable] = [true, true];
            break;
          default:
            const deliveryOptionDiv = document.querySelector(
              `#Filter-Container-Delivery-${store}`
            );
            if (deliveryOptionDiv)
              [deliveryOptionDiv.style.display, storeAvailable] = [
                'block',
                true,
              ];

            const deliveryOptionCC = document.querySelector(
              `#Filter-Delivery-${store}`
            );
            if (deliveryOptionCC) deliveryOptionCC.checked = false;
        }

        if (storeAvailable) processedStores.push(store);
      }
    );

    document
      .querySelector(
        `#Facet-Drawer .thb-filter-count.mobile-filter-count.body-font`
      )
      .classList.remove('loading');

    return processedStores;
  }

  /**
   * Handles visibility toggle of delivery methods.
   *
   * @param deliveryFilter
   * @param displayError
   * @return void
   */
  handleDeliveryErrorMessage(deliveryFilter, displayError = true) {
    if (!['cc-filter', 'sdd-filter'].includes(deliveryFilter)) return;

    // If the delivery filter is not available then don't proceed with the next events.
    if (!document.querySelector(`#${deliveryFilter}-input`)) return;

    document.querySelector(`#${deliveryFilter}-error`).style.display =
      displayError ? 'block' : 'none';

    if (displayError) {
      document
        .querySelector(`#delivery-filter-options`)
        .classList.add('hiding');
    } else {
      document
        .querySelector(`#delivery-filter-options`)
        .classList.remove('hiding');
    }
  }

  /**
   * Provides the list of available stores from the URL.
   *
   * @return {string|*[]}
   */
  getAvailableStoresFromUrl() {
    if (!location.href.includes('?') || location.href.split('?').length < 2)
      return null;

    const params = new URLSearchParams(location.href.split('?')[1]);

    return params.get('filter.available.stores');
  }

  /**
   * Provides a list of all stores that are currently selected for delivery filter.
   */
  getSelectedStores() {
    const selectedStores = document.querySelectorAll(
      `[name="filter.v.m.custom.stores"]:checked`
    );

    const storeValues = [];

    selectedStores.forEach((item) => {
      if (item?.value) storeValues.push(item?.value);
    });

    return storeValues;
  }

  hideDeliveryUnavailabilityMessage() {
    [`#sdd-filter-error`, `#cc-filter-error`].forEach((errorMessageItem) => {
      const messageContent = document.querySelector(errorMessageItem);

      if (!messageContent) return;

      messageContent.style.display = 'none';
    });
  }

  onSubmitHandler(event, params = null) {
    event.preventDefault();

    if(event.target.closest('form').id === 'FacetFiltersFormMobile'){
      document.getElementById("SortBy").value = document.getElementById("SortByMobile").value;
    }

    else if(event.target.id === "SortBy"){
      var mobileFiltersFormData = new FormData(document.getElementById("FacetFiltersFormMobile"));
      mobileFiltersFormData.set("sort_by", document.getElementById("SortBy").value);
      document.getElementById("SortByMobile").value = document.getElementById("SortBy").value;
    }

    const formData = mobileFiltersFormData ?? new FormData(event.target.closest('form'));
    let searchParams = params ?? new URLSearchParams(formData);

    const postcodeFieldValue = document.querySelector('input[name="postcode-field"]')?.value ?? '';

    const postcodeChange = postcodeFieldValue !== this.#postcode;

    // Since the postcode has been entered but neither of the SDD or the CC delivery option is selected, we ignore the request.
    if (postcodeChange) {
      searchParams = this.handlePostcodeEntry(postcodeFieldValue, searchParams);

      this.hideDeliveryUnavailabilityMessage();
    }

    if (
      ((this.#appliedDeliveryFilter &&
        this.#appliedDeliveryFilter !==
          searchParams.get('delivery-filter-options')) ||
        postcodeChange) &&
      !params
    ) {
      this.#resetDeliveryStores();

      searchParams.delete('filter.v.m.custom.stores');
    }

    const selectedDeliveryMethod = document.querySelector(
      'input[name="delivery-filter-options"]:checked'
    )?.value;

    // If the postcode and the delivery filter hasn't changed and is a CC filter then we save the stores list available on the URL.
    if (
      !postcodeChange &&
      this.#appliedDeliveryFilter === selectedDeliveryMethod &&
      selectedDeliveryMethod === 'cc-filter'
    ) {
      searchParams.set(
        'filter.available.stores',
        document.querySelector(`#Filter-Delivery-Available-Stores`).value
      );
    }

    // Handles delivery filter processing.
    if (
      selectedDeliveryMethod &&
      (this.#appliedDeliveryFilter !== selectedDeliveryMethod || postcodeChange)
    ) {
      this.#processDeliveryFilter(
        selectedDeliveryMethod,
        postcodeFieldValue
      ).then((store) => {
        // Handle storage of store information on the URL.
        searchParams.set('filter.available.stores', store.toString());
        document.querySelector(`#Filter-Delivery-Available-Stores`).value =
          store.toString();

        const selectedStores = this.getSelectedStores();

        // Remove the selected stores list as a listing reset
        if (searchParams.get('filter.v.m.custom.stores'))
          searchParams.delete('filter.v.m.custom.stores');

        selectedStores.forEach((store) => {
          searchParams.append('filter.v.m.custom.stores', store);
        });

        // On initial selection of delivery filter option, the selected option is not available on the URL.
        if (
          !searchParams.get('delivery-filter-options') &&
          selectedDeliveryMethod
        ) {
          searchParams.set('delivery-filter-options', selectedDeliveryMethod);
        }

        // Make a call to self again to process new filters.
        this.#appliedDeliveryFilter = selectedDeliveryMethod;
        this.onSubmitHandler(event, searchParams);
      });

      return;
    }

    if (searchParams.get('filter.v.price.gte') === '0.00') {
      searchParams.delete('filter.v.price.gte');
    }
    if (document.querySelector('.price_slider')) {
      if (
        searchParams.get('filter.v.price.lte') ===
        parseFloat(document.querySelector('.price_slider').dataset.max).toFixed(
          2
        )
      ) {
        searchParams.delete('filter.v.price.lte');
      }
    }
    FacetFiltersForm.renderPage(searchParams.toString(), event);
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();

    let url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(
            event.currentTarget.href.indexOf('?') + 1
          );

    let saveAvailableStore = false;

    // If the stores filter data is available then add the available postcode and/or selected filter option to the target URL.
    if (new URLSearchParams(url).get('filter.v.m.custom.stores')) {
      const currentUrlDataArr = location.href.split('?');

      if (currentUrlDataArr.length < 2) FacetFiltersForm.renderPage(url);

      const currentUrlData = new URLSearchParams(currentUrlDataArr[1]);

      if (currentUrlData.get('postcode-field'))
        url = `postcode-field=${currentUrlData.get('postcode-field')}&${url}`;
      if (currentUrlData.get('delivery-filter-options')) {
        url = `delivery-filter-options=${currentUrlData.get(
          'delivery-filter-options'
        )}&${url}`;
        saveAvailableStore = true;
      }
    }

    const availableStores = this.getAvailableStoresFromUrl()?.toString();

    if (url && availableStores && saveAvailableStore)
      url += `&filter.available.stores=${availableStores}`;

    FacetFiltersForm.renderPage(url);
    this.#onFacetsRefresh();
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('a').forEach((item) => {
      item.addEventListener('click', (event) => {
        event.preventDefault();
        const currentTarget = event.currentTarget;
        if (
          currentTarget.classList.contains('active-facets__button-clear-all')
        ) {
          // Clear all filters by reloading the page without query parameters
          window.location.href = window.location.pathname;
          return;
        }

        const form =
          this.closest('facet-filters-form') ||
          document.querySelector('facet-filters-form');
        form.onActiveFilterClick(event);
      });
    });
  }
}

customElements.define('facet-remove', FacetRemove);

/**
 *  @class
 *  @function PriceSlider
 */
class PriceSlider extends HTMLElement {
  constructor() {
    super();
  }
  connectedCallback() {
    let slider = this.querySelector('.price_slider'),
      amounts = this.querySelector('.price_slider_amount'),
      args = {
        start: [
          parseFloat(slider.dataset.minValue || 0),
          parseFloat(slider.dataset.maxValue || slider.dataset.max),
        ],
        connect: true,
        step: 10,
        range: {
          min: 0,
          max: parseFloat(slider.dataset.max),
        },
      },
      event = new CustomEvent('input'),
      form =
        this.closest('facet-filters-form') ||
        document.querySelector('facet-filters-form');

    if (slider.classList.contains('noUi-target')) {
      slider.noUiSlider.destroy();
    }
    noUiSlider.create(slider, args);

    slider.noUiSlider.on('update', function (values) {
      amounts.querySelector('.field__input_min').value = values[0];
      amounts.querySelector('.field__input_max').value = values[1];
    });
    slider.noUiSlider.on('change', function (values) {
      form.querySelector('form').dispatchEvent(event);
    });
  }
}
customElements.define('price-slider', PriceSlider);

window.addEventListener('load', () => {
  new PriceSlider();
  new FacetsToggle();
});
